import { Component, Element, Fragment, h, Method, Prop, State, Event, EventEmitter, Watch } from '@stencil/core';
import { BaseDoc } from 'common';
import { PanZoom } from 'panzoom';
import { PanZoomDisciplineSection, PanZoomPage } from '../models/PanZoom.model';
import { markupsState, NavigationItem, NavigationState } from '..';

export interface PanZoomPagesRendererProps {
  code: PanZoomDisciplineSection;
  pages: PanZoomPage[];
  maxColumns: number;
  pageWidth: number;
  pageHeight: number;
  disciplineHeight: number;
  yOffset: number;
  onPageDblClick: (event: MouseEvent, page: any) => void;
  isSelected: (page: any) => boolean;
}

@Component({
  tag: 'concourse-panzoom',
  styleUrl: 'concourse-panzoom.css',
  shadow: false,
})
export class ConcoursePanZoom {
  @Prop() panzoom: PanZoom;
  @Prop() spacing: number = 40;
  @Prop() pageWidth: number = 800;
  @Prop() pageHeight: number = 600;
  @Prop() maxColumns: number = 4;
  @Prop() autoScaleText: boolean = false;
  @Prop() autoScalePins: boolean = true;
  @Prop() disciplines: PanZoomDisciplineSection[] = [];
  @Prop() pagesByDiscipline: Map<PanZoomDisciplineSection, PanZoomPage[]> = new Map();
  @Prop() pagesRenderer: (props: PanZoomPagesRendererProps) => any[] = () => [];
  @Prop() showDiscipline: boolean = false;
  @Prop() canAddPages: boolean = true;
  @Prop() canAddDisciplines: boolean = true;
  @Prop() toolbarRenderer?: (props: {
    selectedDiscipline: PanZoomDisciplineSection | null;
    position: { x: number; y: number };
    visible: boolean;
    onDisciplineColorChange: (event: { detail: { color: string } }) => void;
    onDisciplineDelete: () => void;
  }) => any;
  @Prop() pageSelectedValue: BaseDoc = null;

  @Element() hostElement: HTMLElement;

  @Event() addNewPage: EventEmitter<PanZoomDisciplineSection>;
  @Event() disciplineUpdate: EventEmitter<{ updatedDiscipline: PanZoomDisciplineSection; originalDiscipline: PanZoomDisciplineSection }>;
  @Event() disciplineDelete: EventEmitter<PanZoomDisciplineSection>;
  @Event() addDiscipline: EventEmitter<number>;
  @Event() touchStartEvent: EventEmitter<TouchEvent>;
  @Event() touchEndEvent: EventEmitter<TouchEvent>;
  @Event() bgDblClick: EventEmitter<MouseEvent>;
  @Event({ bubbles: false }) pageSelected: EventEmitter<any>; // stop this triggering the pageSelected listener in the drawing-page-selector-modal
  @Event({ bubbles: false }) pagesSelected: EventEmitter<any[]>;

  @State() disciplineEditingState = {
    isEditing: false,
    editingDiscipline: null as PanZoomDisciplineSection | null,
    editingText: '',
  };

  @State() selectedDiscipline: PanZoomDisciplineSection | null = null;
  @State() showDisciplineToolbar: boolean = false;
  @State() disciplineToolbarPosition: { x: number; y: number } = { x: 0, y: 0 };
  @State() allPages: any[] = [];
  @State() selectedPage: BaseDoc = null;

  private DISCIPLINE_HEIGHT = 50;
  private TOLERANCE_PADDING = 1000;
  private DISCIPLINE_MIN_WIDTH = 150;

  get panzoomParent(): SVGGraphicsElement {
    return this.hostElement?.querySelector('#panzoom-parent') as SVGGraphicsElement;
  }

  get panzoomElement(): SVGGraphicsElement {
    return this.hostElement?.querySelector('#panzoom-element') as SVGGraphicsElement;
  }

  get pagesLayout(): SVGGraphicsElement {
    return this.hostElement?.querySelector('#pages-layout') as SVGGraphicsElement;
  }

  get pageGroups(): Element[] {
    return Array.from(this.pagesLayout.getElementsByClassName('page-group')) || [];
  }

  get navigationState(): NavigationState {
    return markupsState.navigationState;
  }

  @Watch('pageSelectedValue')
  watchPageSelectedValue() {
    this.selectedPage = this.pageSelectedValue;
  }

  @Method()
  async getPanzoomParent(): Promise<SVGGraphicsElement> {
    return this.panzoomParent;
  }

  @Method()
  async getPanzoomElement(): Promise<SVGGraphicsElement> {
    return this.panzoomElement;
  }

  private actionDebounce: any;
  private bgTouchTime: number = 0;
  private activeTouches = 0;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchStartTime: number = 0;
  private readonly TAP_MAX_DURATION_MS = 250;
  private readonly TAP_MAX_MOVEMENT_PX = 10;
  private middleMouseDownHandler: (e: MouseEvent) => void;
  private middleMouseUpHandler: (e: MouseEvent) => void;
  private wasPanzoomPausedForMarkupInteraction: boolean = false;

  private getGridHeight(pagesByDiscipline: Map<PanZoomDisciplineSection, PanZoomPage[]>): number {
    // loop through all disciplines, based con columns and page height, calculate the max height
    let minHeight = this.pageHeight + this.DISCIPLINE_HEIGHT;
    let height = 0;
    for (const [code, pages] of pagesByDiscipline.entries()) {
      height = this.getDisciplineHeight(code, pages, height, pagesByDiscipline);
    }

    return Math.max(height, minHeight);
  }

  private getGridWidth(pagesByDiscipline: Map<PanZoomDisciplineSection, PanZoomPage[]>): number {
    // loop through all disciplines, and get the max length of the pages
    let minCols = 1;
    let maxLengthColumns = 0;
    for (const [code, pages] of pagesByDiscipline.entries()) {
      // add placeholder column for new page
      maxLengthColumns = Math.max(maxLengthColumns, pages.length + 1);
    }
    let maxCols = maxLengthColumns;
    if (maxCols > this.maxColumns) {
      maxCols = this.maxColumns;
    }

    return Math.max(maxCols, minCols) * (this.pageWidth + this.spacing);
  }

  private getDisciplineHeight(code: PanZoomDisciplineSection, pages: PanZoomPage[], height: number, pagesByDiscipline?: Map<PanZoomDisciplineSection, PanZoomPage[]>): number {
    // Add 1 to account for the "add new page" placeholder
    const exptraPage = this.canAddPages ? 1 : 0;
    const totalPages = pages.length + exptraPage;
    const numRows = Math.ceil(totalPages / this.maxColumns);
    const disciplineKeys = Array.from(pagesByDiscipline.keys());
    const isLastDiscipline = disciplineKeys.lastIndexOf(code) == disciplineKeys.length - 1;
    const extraSpacing = isLastDiscipline ? 0 : this.spacing;
    height += numRows * (this.pageHeight + this.spacing) + this.DISCIPLINE_HEIGHT + extraSpacing;
    return height;
  }

  @Watch('pagesByDiscipline')
  watchPagesByDiscipline() {
    this.updateAllPages();
  }

  private updateAllPages() {
    this.allPages = [];
    for (const [discipline, pages] of this.pagesByDiscipline.entries()) {
      this.allPages.push(...pages.map(page => ({ ...page, discipline })));
    }
  }

  navigateLeft() {
    const pageGroups = this.panzoomParent?.getElementsByClassName('page-group');
    if (!pageGroups || !this.selectedPage) return;

    const currentIndex = Array.from(pageGroups).findIndex(
      child => child.getAttribute('data-page-id') === this.selectedPage._id || child.getAttribute('data-page-rev') === this.selectedPage._rev,
    );
    const prevIndex = currentIndex == 0 ? pageGroups.length - 1 : currentIndex - 1;
    this.navigateToIndex(pageGroups, prevIndex);
  }

  navigateRight() {
    const pageGroups = this.panzoomParent?.getElementsByClassName('page-group');
    if (!pageGroups || !this.selectedPage) return;

    const currentIndex = Array.from(pageGroups).findIndex(
      child => child.getAttribute('data-page-id') === this.selectedPage._id || child.getAttribute('data-page-rev') === this.selectedPage._rev,
    );
    const nextIndex = currentIndex == pageGroups.length - 1 ? 0 : currentIndex + 1;
    this.navigateToIndex(pageGroups, nextIndex);
  }

  navigateUp() {
    const pageGroups = this.panzoomParent?.getElementsByClassName('page-group');
    if (!pageGroups || !this.selectedPage) return;

    const columns = Math.max(Math.floor(Math.sqrt(pageGroups.length)), 1);
    const rows = Math.ceil(pageGroups.length / columns);
    const currentIndex = Array.from(pageGroups).findIndex(
      child => child.getAttribute('data-page-id') === this.selectedPage._id || child.getAttribute('data-page-rev') === this.selectedPage._rev,
    );
    const lastRow = columns * (rows - 1);
    const columnPos = currentIndex % columns;
    const upIndex = currentIndex < columns ? Math.min(lastRow + columnPos, pageGroups.length - 1) : currentIndex - columns;
    this.navigateToIndex(pageGroups, upIndex);
  }

  navigateDown() {
    const pageGroups = this.panzoomParent?.getElementsByClassName('page-group');
    if (!pageGroups || !this.selectedPage) return;

    const columns = Math.max(Math.floor(Math.sqrt(pageGroups.length)), 1);
    const rows = Math.ceil(pageGroups.length / columns);
    const currentIndex = Array.from(pageGroups).findIndex(
      child => child.getAttribute('data-page-id') === this.selectedPage._id || child.getAttribute('data-page-rev') === this.selectedPage._rev,
    );
    const columnPos = currentIndex % columns;
    const lastRow = columns * (rows - 1);
    const downIndex = currentIndex > lastRow ? columnPos : currentIndex + columns;
    this.navigateToIndex(pageGroups, downIndex);
  }

  navigateToIndex(pageGroups: HTMLCollectionOf<Element>, index: number) {
    if (typeof pageGroups[index] === 'undefined') return;
    const pageId = pageGroups[index].getAttribute('data-page-id');
    const pageRev = pageGroups[index].getAttribute('data-page-rev');
    const page = this.allPages.find(p => p.id === pageId || p._rev === pageRev);
    if (page) {
      this.selectPage(page);
    }
  }

  selectPage(page: any) {
    this.selectedPage = page;
    this.pageSelected.emit(page);
    this.pagesSelected.emit([page]);
  }

  @Method()
  async getElementMostOnScreen(threshold: number): Promise<SVGGElement> {
    const parentRect = this.panzoomParent?.getBoundingClientRect();
    const ctm = this.panzoomElement?.getScreenCTM();
    if (!parentRect || !ctm) return null;

    const parentArea = parentRect.width * parentRect.height;
    const pageElements: NodeListOf<SVGGElement> = this.panzoomParent?.querySelectorAll('.page-group');
    if (!pageElements?.length) return null;

    const percentages = Array.from(pageElements).map(element => {
      const childRect = element?.getBBox();
      if (!childRect) {
        return null;
      }

      const screenX = ctm.e + Math.abs(childRect.x * ctm.a);
      const screenY = ctm.f + Math.abs(childRect.y * ctm.d);
      const screenWidth = Math.abs(childRect.width * ctm.a);
      const screenHeight = Math.abs(childRect.height * ctm.d);
      const screenRight = screenX + screenWidth;
      const screenBottom = screenY + screenHeight;

      const overlapX = Math.max(0, Math.min(screenRight, parentRect.right) - Math.max(screenX, parentRect.left));
      const overlapY = Math.max(0, Math.min(screenBottom, parentRect.bottom) - Math.max(screenY, parentRect.top));

      const visibleArea = overlapX * overlapY;
      const percentageVisible = (visibleArea / parentArea) * 100;
      return {
        element: element,
        percentageVisible: Math.min(100, Math.max(0, percentageVisible)),
      };
    });

    const mostVisible = percentages.reduce((prev, curr) => (prev.percentageVisible > curr.percentageVisible ? prev : curr));
    if (mostVisible.percentageVisible > threshold) {
      return mostVisible.element;
    }
    return null;
  }

  isSelected(page: any): boolean {
    return this.selectedPage && (this.selectedPage._id === page.id || this.selectedPage._rev === page._rev);
  }

  onPageDblClick(event: MouseEvent, page: any) {
    event.stopPropagation();
    if (page && !this.isSelected(page)) {
      this.selectPage(page);
    } else {
      this.selectedPage = null;
      this.pageSelected.emit(null);
      this.pagesSelected.emit(null);
    }
  }

  @Method()
  async zoomToViewAll() {
    if (this.pagesLayout) {
      this.zoomToRect(this.pagesLayout);
    } else {
    }
  }

  @Method()
  async zoomToDiscipline(discipline?: PanZoomDisciplineSection) {
    if (!discipline) return;

    const disciplineRow = this.panzoomParent?.querySelector(`[data-code="${discipline._id}"]`) as SVGGraphicsElement;
    if (disciplineRow) {
      this.zoomToRect(disciplineRow);
    }
  }

  @Method()
  async scaleText(pageGroups: any[]) {
    const scale = Math.max(0.2, this.panzoom.getTransform().scale);
    pageGroups.forEach((group: SVGGraphicsElement) => {
      if (!group.querySelector('text')) return;

      const textElement = group.querySelector('text');
      if (textElement && textElement.getAttribute('class').includes('scaled-text')) {
        textElement.setAttribute('font-size', `${40 / scale}`);
      }
    });
  }

  @Method()
  async scaleRfis(pageGroups: any[]) {
    if (!this.panzoom) {
      return;
    }

    const scale = Math.max(0.2, this.panzoom.getTransform().scale);
    pageGroups.forEach((group: SVGGraphicsElement) => {
      const pinElements = group.querySelectorAll('.rfi-scalable');
      pinElements.forEach((pinElement: SVGGraphicsElement) => {
        const currentTransform = pinElement.getAttribute('transform') || '';
        const baseScale = 1.333;
        const adjustedScale = Math.max(baseScale, baseScale / scale);
        const newTransform = currentTransform.replace(/scale\([\d.]+\)/, `scale(${adjustedScale})`);
        pinElement.setAttribute('transform', newTransform);
      });
    });
  }

  async disableRfiScales() {
    this.pageGroups.forEach((group: SVGGraphicsElement) => {
      const pinElements = group.querySelectorAll('.rfi-scalable');
      pinElements.forEach((pinElement: SVGGraphicsElement) => {
        const currentTransform = pinElement.getAttribute('transform') || '';
        const baseScale = 0.0625;
        const newTransform = currentTransform.replace(/scale\([\d.]+\)/, `scale(${baseScale})`);
        pinElement.setAttribute('transform', newTransform);
      });
    });
  }

  @Watch('autoScalePins')
  changeAutoScalePins(autoScalePins: boolean) {
    if (autoScalePins) {
      this.scaleRfis(this.pageGroups);
    } else {
      this.disableRfiScales();
    }
  }

  @Method()
  async zoomToPage(page?: BaseDoc) {
    const targetPage = page || this.selectedPage;
    if (targetPage) {
      const group = this.getPageGroup(targetPage);
      if (group) {
        this.zoomToRect(group);
      }
    }
  }

  getPageGroup(page: BaseDoc): SVGGElement {
    const pageGroups = this.panzoomParent?.getElementsByClassName('page-group');
    if (!pageGroups) {
      return null;
    }
    const foundElement = Array.from(pageGroups).find(child => child.getAttribute('data-page-id') === page._id || child.getAttribute('data-page-rev') === page._rev);
    return foundElement as SVGGElement;
  }

  private zoomToRect(element: SVGGraphicsElement) {
    if (!this.panzoom || !element || !element.getBBox) {
      return;
    }

    // Get the element's bounding box in the coordinate system of the panzoom element
    const elementBBox = element.getBBox();

    // Get the element's transform relative to the panzoom element (not screen)
    const panzoomCTM = this.panzoomElement.getCTM();
    const elementCTM = element.getCTM();

    let actualX = elementBBox.x;
    let actualY = elementBBox.y;

    if (elementCTM && panzoomCTM) {
      // Calculate position relative to panzoom element by removing panzoom transform
      actualX = (elementCTM.e - panzoomCTM.e) / panzoomCTM.a + elementBBox.x;
      actualY = (elementCTM.f - panzoomCTM.f) / panzoomCTM.d + elementBBox.y;
    }

    const parentBBox = this.panzoomParent.getBoundingClientRect();

    const margin = 40;
    const expandedWidth = elementBBox.width + margin * 2;
    const expandedHeight = elementBBox.height + margin * 2;

    const scaleX = parentBBox.width / expandedWidth;
    const scaleY = parentBBox.height / expandedHeight;
    const targetScale = Math.min(scaleX, scaleY);

    const elementCenterX = actualX + elementBBox.width / 2;
    const elementCenterY = actualY + elementBBox.height / 2;
    const parentCenterX = parentBBox.width / 2;
    const parentCenterY = parentBBox.height / 2;

    const targetX = parentCenterX - targetScale * elementCenterX;
    const targetY = parentCenterY - targetScale * elementCenterY;

    const { x: currentX, y: currentY, scale: currentScale } = this.panzoom.getTransform();

    if (currentScale === targetScale) {
      this.panzoom.smoothMoveTo(targetX, targetY);
      return;
    }

    const ux = (targetX - currentX) / (currentScale - targetScale);
    const uy = (targetY - currentY) / (currentScale - targetScale);

    const anchorScreenX = currentX + currentScale * ux;
    const anchorScreenY = currentY + currentScale * uy;

    this.panzoom.smoothZoomAbs(anchorScreenX, anchorScreenY, targetScale);
  }

  @Method()
  async initializePanZoom() {
    if (!this.panzoomElement) {
      return;
    }

    this.setupKeyboardNavigation();
    this.setupPanZoomEvents();
    this.updateAllPages();
    if (this.autoScalePins) {
      this.scaleRfis(this.pageGroups);
    }
  }

  private setupKeyboardNavigation() {
    document.addEventListener(
      'keyup',
      (event: KeyboardEvent) => {
        if (!this.selectedPage) {
          return;
        }

        switch (event.code) {
          case 'ArrowLeft':
            this.navigateLeft();
            break;
          case 'ArrowUp':
            this.navigateUp();
            break;
          case 'ArrowRight':
            this.navigateRight();
            break;
          case 'ArrowDown':
            this.navigateDown();
            break;
        }
      },
      false,
    );
  }

  @Watch('panzoom')
  watchPanzoom(panzoom: PanZoom) {
    if (!!panzoom) {
      this.initializePanZoom();
    }
  }

  private setupPanZoomEvents() {
    if (!this.panzoom) return;

    this.panzoom.on('transform', () => {
      if (this.autoScaleText) {
        this.scaleText(this.pageGroups);
      }
      if (this.autoScalePins) {
        this.scaleRfis(this.pageGroups);
      }
    });

    // Middle mouse button panzoom control
    this.middleMouseDownHandler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const selectedElementId = markupsState.selectionState.selectedElementId;
      const selectedMarkupElement = selectedElementId ? this.hostElement?.querySelector(`[data-element-id="${selectedElementId}"]`) : null;
      const clickedSelectedMarkupElement = !!selectedMarkupElement && !!target?.closest(`[data-element-id="${selectedElementId}"]`);
      const clickedMarkupElement =
        !!target &&
        (target.closest('.scrapbook-element') ||
          target.closest('.moveable-control') ||
          target.closest('.moveable-line') ||
          target.closest('.moveable-area') ||
          target.closest('.text-editing-input'));
      const clickedMoveableUi = !!target && (!!target.closest('.moveable-control') || !!target.closest('.moveable-line') || !!target.closest('.moveable-area'));

      this.wasPanzoomPausedForMarkupInteraction = false;
      const shouldPauseForMarkupDrag = e.button !== 1 && clickedMarkupElement && (clickedSelectedMarkupElement || (clickedMoveableUi && !!selectedElementId));
      if (shouldPauseForMarkupDrag) {
        this.panzoom?.pause();
        this.wasPanzoomPausedForMarkupInteraction = true;
      }
    };

    this.middleMouseUpHandler = (e: MouseEvent) => {
      if (e.button !== 1 && this.wasPanzoomPausedForMarkupInteraction) {
        this.panzoom?.resume();
        this.wasPanzoomPausedForMarkupInteraction = false;
      }
    };

    this.panzoomElement.addEventListener('mousedown', this.middleMouseDownHandler);
    this.panzoomElement.addEventListener('mouseup', this.middleMouseUpHandler);
  }

  componentDidLoad() {
    if (this.panzoom) {
      this.initializePanZoom();
    }
  }

  disconnectedCallback() {
    if (this.panzoom) {
      this.panzoom.dispose();
    }
    if (this.actionDebounce) {
      clearTimeout(this.actionDebounce);
    }
    if (this.panzoomElement) {
      if (this.middleMouseDownHandler) {
        this.panzoomElement.removeEventListener('mousedown', this.middleMouseDownHandler);
      }
      if (this.middleMouseUpHandler) {
        this.panzoomElement.removeEventListener('mouseup', this.middleMouseUpHandler);
      }
    }
  }

  private renderAddNewPagePlaceholder(discipline: PanZoomDisciplineSection, pages: any[]): SVGElement {
    const totalPages = pages.length;
    const col = totalPages % this.maxColumns;
    const row = Math.floor(totalPages / this.maxColumns);
    const x = col * (this.pageWidth + this.spacing);
    const y = this.DISCIPLINE_HEIGHT + row * (this.pageHeight + this.spacing);

    return (
      <g class="add-new-page-placeholder" onClick={() => this.addNewPage.emit(discipline)}>
        <rect
          x={x}
          y={y}
          width={this.pageWidth}
          height={this.pageHeight}
          fill="transparent"
          stroke="#ccc"
          stroke-width="2"
          stroke-dasharray="10,5"
          rx="8"
          ry="8"
          style={{ cursor: 'pointer' }}
        />
        <text
          x={x + this.pageWidth / 2}
          y={y + this.pageHeight / 2}
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="24"
          fill="#ccc"
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          + Add Page
        </text>
      </g>
    );
  }

  getTextWidth(text: string, font = '56px Arial'): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    return context.measureText(text).width;
  }

  private getPanZoomView() {
    const pagesByDiscipline = this.pagesByDiscipline;
    let yOffset = 0; // tracks vertical stacking of rows
    const sectionElements = Array.from(pagesByDiscipline.entries()).map(([discipline, pages]: [PanZoomDisciplineSection, PanZoomPage[]], index) => {
      const pagesElements = this.pagesRenderer({
        code: discipline,
        pages,
        maxColumns: this.maxColumns,
        pageWidth: this.pageWidth,
        pageHeight: this.pageHeight,
        disciplineHeight: this.DISCIPLINE_HEIGHT,
        yOffset,
        onPageDblClick: (event, page) => this.onPageDblClick(event, page),
        isSelected: page => this.isSelected(page),
      });
      const addNewPageElement = this.renderAddNewPagePlaceholder(discipline, pages);
      const disciplineRowId = `discipline-row-${index}`;
      const layout = (
        <g class="discipline-row" data-code={discipline._id}>
          {discipline.showHeader && this.showDiscipline ? (
            <Fragment>
              <rect
                id={disciplineRowId}
                x={0}
                y={yOffset}
                width={Math.max(this.getTextWidth(discipline.name), this.DISCIPLINE_MIN_WIDTH)}
                height={this.DISCIPLINE_HEIGHT}
                rx="10"
                ry="10"
                fill={this.selectedDiscipline?.name === discipline.name ? '#ddeeff' : 'LightGray'}
                stroke={this.selectedDiscipline?.name === discipline.name ? '#007bff' : 'transparent'}
                stroke-width="2"
                style={{ cursor: 'pointer' }}
              />
              <text
                x={5}
                y={yOffset + 5}
                font-size="48"
                font-weight="bold"
                fill={discipline.color}
                dominant-baseline="hanging"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={e => {
                  e.stopPropagation();
                  this.selectDiscipline(discipline, disciplineRowId);
                }}
                onDblClick={e => {
                  e.stopPropagation();
                  this.startDisciplineNameEditing(discipline, disciplineRowId);
                }}
              >
                {discipline.name}
              </text>
            </Fragment>
          ) : (
            this.canAddDisciplines && this.renderAddDisciplinePlaceholder(yOffset)
          )}
          {pagesElements}
          {this.canAddPages && <g transform={`translate(0, ${yOffset})`}>{addNewPageElement}</g>}
        </g>
      );

      yOffset = this.getDisciplineHeight(discipline, pages, yOffset, pagesByDiscipline);
      return layout;
    });

    const gridWidth = this.getGridWidth(pagesByDiscipline);
    const gridHeight = this.getGridHeight(pagesByDiscipline);

    return (
      <svg id="panzoom-parent" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" fill="#666">
        <defs>
          <pattern id="gridPattern" x="0" y="0" width="115" height="115" patternUnits="userSpaceOnUse">
            <rect width="115" height="115" fill="#666" stroke="gray" stroke-width="0.5" />
          </pattern>
        </defs>
        <g id="panzoom-element" onTouchStart={e => this.onTouchStart(e)} onTouchEnd={e => this.onTouchEnd(e)}>
          <rect
            class="panzoom-grid-background"
            x={-this.TOLERANCE_PADDING}
            y={-this.TOLERANCE_PADDING}
            width={gridWidth + 2 * this.TOLERANCE_PADDING}
            height={gridHeight + 2 * this.TOLERANCE_PADDING}
            fill="url(#gridPattern)"
            onClick={e => {
              this.selectDiscipline(null, null);
            }}
            onDblClick={e => this.onBgDblClick(e)}
          />
          <g id="pages-layout">
            {sectionElements}
            <g>{this.canAddDisciplines && this.renderAddDisciplinePlaceholder(gridHeight)}</g>
          </g>
        </g>
      </svg>
    );
  }

  private selectDiscipline(discipline: PanZoomDisciplineSection | null, disciplineRowId: string | null) {
    this.selectedDiscipline = discipline;
    this.showDisciplineToolbar = !!discipline;
    this.positionDisciplineToolbar(disciplineRowId);
  }

  private renderAddDisciplinePlaceholder(yOffset: number): SVGElement {
    if (!this.showDiscipline) {
      return null;
    }

    const placeholderWidth = Math.max(200, this.DISCIPLINE_MIN_WIDTH);
    return (
      <g class="add-discipline-placeholder">
        <rect
          x={0}
          y={yOffset}
          width={placeholderWidth}
          height={this.DISCIPLINE_HEIGHT}
          fill="transparent"
          stroke="#ccc"
          stroke-width="2"
          stroke-dasharray="10,5"
          rx="10"
          ry="10"
          style={{ cursor: 'pointer' }}
          onClick={e => {
            e.stopPropagation();
            this.addDiscipline.emit(yOffset);
          }}
        />
        <text
          x={placeholderWidth / 2}
          y={yOffset + this.DISCIPLINE_HEIGHT / 2}
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="24"
          fill="#ccc"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={e => {
            e.stopPropagation();
            this.addDiscipline.emit(yOffset);
          }}
        >
          + Add Section
        </text>
      </g>
    );
  }

  private startDisciplineNameEditing(discipline: PanZoomDisciplineSection, disciplineRowId: string) {
    this.disciplineEditingState = {
      isEditing: true,
      editingDiscipline: discipline,
      editingText: discipline.name,
    };

    setTimeout(() => {
      this.createDisciplineEditingElement(discipline, disciplineRowId);
    }, 50);
  }

  private createDisciplineEditingElement(discipline: PanZoomDisciplineSection, disciplineRowId: string) {
    const panzoomElement = this.panzoomElement;
    if (!panzoomElement) {
      return;
    }

    const existingInput = panzoomElement.querySelector('.discipline-editing-input');
    if (existingInput) {
      existingInput.remove();
    }

    const disciplineRow = panzoomElement.querySelector(`#${disciplineRowId}`);
    if (!disciplineRow) {
      return;
    }

    const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
    foreignObject.setAttribute('x', disciplineRow.getAttribute('x'));
    foreignObject.setAttribute('y', disciplineRow.getAttribute('y'));
    foreignObject.setAttribute('width', disciplineRow.getAttribute('width'));
    foreignObject.setAttribute('height', disciplineRow.getAttribute('height'));
    foreignObject.setAttribute('class', 'discipline-editing-container');

    const input = document.createElement('input');
    input.className = 'discipline-editing-input';
    input.type = 'text';
    input.value = this.disciplineEditingState.editingText;

    Object.assign(input.style, {
      width: '100%',
      height: '100%',
      fontSize: '48px',
      fontWeight: 'bold',
      border: '2px solid #007bff',
      outline: 'none',
      padding: '2px 4px',
      fontFamily: 'inherit',
      backgroundColor: 'white',
      color: discipline.color,
      margin: '0',
      boxSizing: 'border-box',
    });

    input.addEventListener('input', e => this.onDisciplineInputChange(e));
    input.addEventListener('keydown', e => this.onDisciplineInputKeyDown(e));
    input.addEventListener('blur', () => this.finishDisciplineEditing(discipline));

    input.addEventListener('mousedown', e => e.stopPropagation());
    input.addEventListener('mousemove', e => e.stopPropagation());
    input.addEventListener('mouseup', e => e.stopPropagation());
    input.addEventListener('click', e => e.stopPropagation());

    foreignObject.appendChild(input);
    panzoomElement.appendChild(foreignObject);

    setTimeout(() => {
      input.focus();
      input.select();
    }, 10);
  }

  private finishDisciplineEditing(discipline: PanZoomDisciplineSection) {
    if (!this.disciplineEditingState.isEditing || !this.disciplineEditingState.editingDiscipline) return;

    const newName = this.disciplineEditingState.editingText.trim();
    if (newName && newName !== this.disciplineEditingState.editingDiscipline.name) {
      const updatedDiscipline = { ...discipline, name: newName };
      this.disciplineUpdate.emit({ updatedDiscipline, originalDiscipline: this.disciplineEditingState.editingDiscipline });
    }

    this.cleanupDisciplineEditing();
  }

  private cancelDisciplineEditing() {
    this.cleanupDisciplineEditing();
  }

  private cleanupDisciplineEditing() {
    const panzoomElement = this.panzoomElement;
    const editingContainer = panzoomElement?.querySelector('.discipline-editing-container');
    if (editingContainer) {
      editingContainer.remove();
    }

    this.disciplineEditingState = {
      isEditing: false,
      editingDiscipline: null,
      editingText: '',
    };
  }

  private onDisciplineInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.disciplineEditingState = {
      ...this.disciplineEditingState,
      editingText: target.value,
    };
  }

  private onDisciplineInputKeyDown(event: KeyboardEvent) {
    event.stopPropagation();

    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.disciplineEditingState.editingDiscipline) {
        this.finishDisciplineEditing(this.disciplineEditingState.editingDiscipline);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelDisciplineEditing();
    }
  }

  private updateDisciplineColor(color: string) {
    if (this.selectedDiscipline) {
      const updatedDiscipline = { ...this.selectedDiscipline, color };
      this.disciplineUpdate.emit({ updatedDiscipline, originalDiscipline: this.selectedDiscipline });
      this.selectedDiscipline = updatedDiscipline;
    }
  }

  private deleteDiscipline() {
    if (this.selectedDiscipline) {
      this.disciplineDelete.emit(this.selectedDiscipline);
      this.selectedDiscipline = null;
      this.showDisciplineToolbar = false;
    }
  }

  private onTouchStart(e: TouchEvent) {
    this.activeTouches += e.changedTouches.length;
    if (e.changedTouches.length === 1) {
      this.touchStartX = e.changedTouches[0].clientX;
      this.touchStartY = e.changedTouches[0].clientY;
      this.touchStartTime = Date.now();
    }
    this.touchStartEvent.emit(e);
  }

  private onTouchEnd(e: TouchEvent) {
    this.activeTouches -= e.changedTouches.length;
    if (this.activeTouches > 0) return;

    const touch = e.changedTouches[0];
    const duration = Date.now() - this.touchStartTime;
    const dx = Math.abs(touch.clientX - this.touchStartX);
    const dy = Math.abs(touch.clientY - this.touchStartY);
    const isTap = duration < this.TAP_MAX_DURATION_MS && dx < this.TAP_MAX_MOVEMENT_PX && dy < this.TAP_MAX_MOVEMENT_PX;

    if (isTap) {
      const timeBetweenTouches = Date.now() - this.bgTouchTime;
      if (timeBetweenTouches < 500 && timeBetweenTouches > 0) {
        const element = touch.target as SVGGraphicsElement;
        const pageGroup = element.closest('.page-group') as SVGGraphicsElement;
        if (pageGroup) {
          const pageId = pageGroup.getAttribute('data-page-id');
          const pageRev = pageGroup.getAttribute('data-page-rev');
          const page = this.allPages.find(p => p.id === pageId || p._rev === pageRev);
          if (page) {
            this.selectPage(page);
          }
        }
      } else {
        this.bgTouchTime = Date.now();
      }

      // Dispatch a synthetic click so that onClick handlers on elements inside
      // the panzoom (e.g. markup element selection) fire on mobile, where the
      // panzoom library's preventDefault() suppresses the browser-generated click.
      const syntheticClick = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        clientX: touch.clientX,
        clientY: touch.clientY,
        view: window,
      });
      touch.target.dispatchEvent(syntheticClick);
    }

    this.touchEndEvent.emit(e);
  }

  private positionDisciplineToolbar(disciplineRowId: string) {
    // Find the actual discipline rect element to get screen coordinates
    const disciplineRow = this.hostElement.querySelector(`#${disciplineRowId}`);
    if (!disciplineRow) {
      return;
    }
    const elementRect = disciplineRow.getBoundingClientRect();
    const canvasRect = this.hostElement?.querySelector('#panzoom-parent')?.getBoundingClientRect();
    if (!canvasRect) {
      return;
    }
    const toolbarX = elementRect.right + canvasRect.left;
    const toolbarY = elementRect.bottom - canvasRect.top;

    this.disciplineToolbarPosition = { x: toolbarX, y: toolbarY };
  }

  private onBgDblClick(e: MouseEvent) {
    e.stopPropagation();
    this.pageSelected.emit(null);
    this.pagesSelected.emit(null);
    this.bgDblClick.emit(e);
  }

  render() {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {this.getPanZoomView()}
        {this.showDisciplineToolbar && this.selectedDiscipline && this.renderToolbar()}
      </div>
    );
  }

  private renderToolbar() {
    return this.toolbarRenderer({
      selectedDiscipline: this.selectedDiscipline,
      position: this.disciplineToolbarPosition,
      visible: this.showDisciplineToolbar,
      onDisciplineColorChange: event => this.updateDisciplineColor(event.detail.color),
      onDisciplineDelete: () => this.deleteDiscipline(),
    });
  }
}
